/**
 * useAppStore — desktop counterpart to BigxBookApp/src/store/useAppStore.ts.
 *
 * Holds the slice of global state the sync engine and other ported
 * modules read from. Persistence:
 *   - Auth tokens (authToken, refreshToken) live in OS secret storage
 *     (DPAPI on Windows) via httpClient.tokenStorage. They are mirrored
 *     into this in-memory store on startup via `hydrateFromStorage()`.
 *   - activeStoreId, deviceId, deviceSlot, currentUser persist via kvStore
 *     (electron-store JSON file). Mirror is rebuilt on startup.
 *   - Sync state (isSyncing, lastSyncAt, syncError) is in-memory only —
 *     same lifetime semantics as BigxBook where these aren't persisted.
 */
import { create } from 'zustand';
import { kvStore } from '@/platform';
import { ensureAccessToken, tokenStorage } from '../services/httpClient';
import { onAccessTokenChange } from '../services/tokenStore';
import { activateStore } from '@/db';
import { localHighestSequence } from '@/billing/invoiceNumberRecovery';

export interface UserInfo {
  id: string;
  phone: string;
  name?: string;
  role?: string;
  merchantId: string;
  onboardingStatus?: string;
}

interface AppState {
  hasHydrated: boolean;

  // Auth
  authToken: string | null;
  refreshToken: string | null;
  currentUser: UserInfo | null;
  setAuthToken: (token: string | null) => void;
  setRefreshToken: (token: string | null) => void;
  setCurrentUser: (user: UserInfo | null) => Promise<void>;
  logout: () => Promise<void>;

  // Core
  activeStoreId: string | null;
  setActiveStore: (id: string | null) => Promise<void>;

  // Sync
  isSyncing: boolean;
  setIsSyncing: (v: boolean) => void;
  lastSyncAt: number | null;
  setLastSyncAt: (ts: number) => void;
  syncError: string | null;
  setSyncError: (err: string | null) => void;

  // Device
  deviceId: string | null;
  setDeviceId: (id: string | null) => Promise<void>;
  deviceSlot: number | null;
  setDeviceSlot: (slot: number | null) => Promise<void>;

  // Device-local invoice sequence (persisted, monotonic, collision-proof)
  deviceInvoiceCounter: number;
  /**
   * Why this device cannot bill, when it cannot.
   *
   * Null means either "fine" or "not asked yet"; the slot itself is the source
   * of truth for whether billing is allowed. This exists so the refusal can
   * name its cause instead of offering the same generic "connect to the
   * internet once" for a device that is online and simply not registered.
   */
  deviceSlotError: string | null;
  setDeviceSlotError: (reason: string | null) => void;
  nextDeviceInvoiceNo: () => number;
  /**
   * Raise the sequence to at least `floor`. Never lowers it: numbers already
   * handed out stay spent even if the server has not seen them yet.
   */
  raiseDeviceInvoiceCounter: (floor: number) => Promise<void>;
}

const KEY_USER = 'app.currentUser';
const KEY_STORE = 'app.activeStoreId';
const KEY_DEVICE_ID = 'device.id';
const KEY_DEVICE_SLOT = 'app.deviceSlot';
const KEY_INVOICE_COUNTER = 'app.deviceInvoiceCounter';
// WEB ADAPTATION: Desktop keeps lastSyncAt in memory only. Web must persist it,
// because the stale-sync gate (PLAN Decision 7) has to know how old the local
// catalogue is after a reload — and a browser reloads far more often than a
// desktop app restarts. Without this every reload would look like 'never synced'.
const KEY_LAST_SYNC_AT = 'app.lastSyncAt';

// Module-level spinlock — prevents two synchronous nextDeviceInvoiceNo() calls
// (e.g. button spam) from reading the same counter value.
let _invoiceCounterLock = false;

export const useAppStore = create<AppState>((set) => ({
  hasHydrated: false,

  authToken: null,
  refreshToken: null,
  currentUser: null,
  setAuthToken: (token) => set({ authToken: token }),
  setRefreshToken: (token) => set({ refreshToken: token }),
  setCurrentUser: async (user) => {
    set({ currentUser: user });
    if (user) await kvStore.set(KEY_USER, JSON.stringify(user));
    else await kvStore.delete(KEY_USER);
  },
  logout: async () => {
    await tokenStorage.clear();
    await Promise.all([
      kvStore.delete(KEY_USER),
      kvStore.delete(KEY_STORE),
      kvStore.delete(KEY_DEVICE_SLOT),
      // The invoice counter belongs to a store, not to the browser.
      //
      // Left behind, it follows the machine into the NEXT merchant who signs
      // in on it: a brand-new shop's very first bill came out as SHS-00000012,
      // with 1 to 11 simply absent from its series. A tax invoice series with
      // holes in it is the kind of thing an assessing officer asks about.
      //
      // Safe to drop, because recoverInvoiceSequence() raises the counter past
      // whatever that store has already issued on the next sync, and
      // useCreateInvoice checks for a local collision before using a number.
      kvStore.delete(KEY_INVOICE_COUNTER),
    ]);
    set({
      authToken: null,
      refreshToken: null,
      currentUser: null,
      activeStoreId: null,
      deviceSlot: null,
      deviceInvoiceCounter: 0,
      lastSyncAt: null,
      syncError: null,
      isSyncing: false,
    });
  },

  activeStoreId: null,
  setActiveStore: async (id) => {
    // WEB ADAPTATION: Desktop has one Database created at module load. Web keeps
    // one per store, so switching stores must also repoint the `database` proxy
    // — otherwise the next query reads the previous store, or throws. Doing it
    // here means callers cannot forget the second step.
    if (id) activateStore(id);
    const previous = useAppStore.getState().activeStoreId;
    set({ activeStoreId: id });
    if (id) await kvStore.set(KEY_STORE, id);
    else await kvStore.delete(KEY_STORE);

    // Changing store means changing invoice series. Carrying the counter across
    // would start the new store's numbering wherever the old one left off.
    // recoverInvoiceSequence() lifts it back to the right place on the next
    // sync for a store that already has bills.
    if (previous && previous !== id) {
      set({ deviceInvoiceCounter: 0 });
      await kvStore.delete(KEY_INVOICE_COUNTER);
    }
  },

  isSyncing: false,
  setIsSyncing: (v) => set({ isSyncing: v }),
  lastSyncAt: null,
  setLastSyncAt: (ts) => {
    set({ lastSyncAt: ts });
    // Fire-and-forget: the gate tolerates a slightly stale value, and
    // setLastSyncAt is sync on Desktop so the signature must not change.
    void kvStore.set(KEY_LAST_SYNC_AT, String(ts));
  },
  syncError: null,
  setSyncError: (err) => set({ syncError: err }),

  deviceId: null,
  setDeviceId: async (id) => {
    set({ deviceId: id });
    if (id) await kvStore.set(KEY_DEVICE_ID, id);
    else await kvStore.delete(KEY_DEVICE_ID);
  },
  deviceSlot: null,
  setDeviceSlot: async (slot) => {
    set({ deviceSlot: slot });
    if (slot !== null && slot !== undefined) {
      await kvStore.set(KEY_DEVICE_SLOT, String(slot));
    } else {
      await kvStore.delete(KEY_DEVICE_SLOT);
    }
  },

  deviceInvoiceCounter: 0,
  deviceSlotError: null,
  setDeviceSlotError: (reason) => useAppStore.setState({ deviceSlotError: reason }),
  nextDeviceInvoiceNo: (): number => {
    if (_invoiceCounterLock) {
      console.warn('[AppStore] nextDeviceInvoiceNo called while locked — safe fallback');
    }
    _invoiceCounterLock = true;
    try {
      const current = useAppStore.getState().deviceInvoiceCounter;
      if (current >= 999999) {
        throw new Error('Invoice sequence limit reached (999999). Please contact support.');
      }
      const next = current + 1;
      useAppStore.setState({ deviceInvoiceCounter: next });
      // Fire-and-forget persist (sequence is recoverable from server max on next sync).
      kvStore.set(KEY_INVOICE_COUNTER, String(next)).catch(() => undefined);
      return next;
    } finally {
      _invoiceCounterLock = false;
    }
  },

  raiseDeviceInvoiceCounter: async (floor: number): Promise<void> => {
    if (!Number.isFinite(floor) || floor <= 0) return;
    const current = useAppStore.getState().deviceInvoiceCounter;
    if (floor <= current) return;
    useAppStore.setState({ deviceInvoiceCounter: floor });
    // Persisted eagerly, unlike the increment above: this path exists precisely
    // because the persisted value was lost, so losing it again is the failure
    // mode being fixed.
    try {
      await kvStore.set(KEY_INVOICE_COUNTER, String(floor));
    } catch {
      /* storage refused — the next sync recovers it again */
    }
    console.warn(
      `[AppStore] invoice sequence recovered ${current} → ${floor} ` +
      `(local counter was behind the invoices this device has already issued)`,
    );
  },
}));

/**
 * WEB ADAPTATION: keep `authToken` in step with the real token.
 *
 * The access token lives in memory in tokenStore and is refreshed there by
 * httpClient. Ported modules read this mirror instead — `sync/index.ts` uses it
 * as both the auth check and the Bearer token — so if it goes stale, sync
 * returns early with no error and the UI shows a frozen last-sync time.
 */
onAccessTokenChange((token) => {
  useAppStore.setState({ authToken: token });
});

/**
 * Read persisted values from kvStore + secureStore and seed the in-memory
 * store. Call exactly once at app startup, before any sync attempt.
 */
export async function hydrateAppStore(): Promise<void> {
  const [userJson, storeId, deviceId, deviceSlotStr, invoiceCounterStr, lastSyncAtStr, accessToken, refreshToken] = await Promise.all([
    kvStore.getString(KEY_USER),
    kvStore.getString(KEY_STORE),
    kvStore.getString(KEY_DEVICE_ID),
    kvStore.getString(KEY_DEVICE_SLOT),
    kvStore.getString(KEY_INVOICE_COUNTER),
    kvStore.getString(KEY_LAST_SYNC_AT),
    ensureAccessToken(),
    tokenStorage.getRefreshToken(),
  ]);

  let currentUser: UserInfo | null = null;
  if (userJson) {
    try { currentUser = JSON.parse(userJson) as UserInfo; }
    catch { /* ignore corrupt blob */ }
  }

  const deviceSlot = deviceSlotStr ? Number.parseInt(deviceSlotStr, 10) : null;
  const invoiceCounter = invoiceCounterStr ? Number.parseInt(invoiceCounterStr, 10) : 0;
  const lastSyncAt = lastSyncAtStr ? Number.parseInt(lastSyncAtStr, 10) : null;

  // WEB ADAPTATION: repoint the per-store database proxy before any ported
  // module touches `database`. Without this, a reload restores activeStoreId in
  // memory while the proxy has no active store, and the first query throws.
  if (storeId) activateStore(storeId);

  useAppStore.setState({
    hasHydrated: true,
    authToken: accessToken,
    refreshToken,
    currentUser,
    activeStoreId: storeId,
    deviceId,
    deviceSlot: Number.isFinite(deviceSlot as number) ? deviceSlot : null,
    deviceInvoiceCounter: Number.isFinite(invoiceCounter) ? invoiceCounter : 0,
    lastSyncAt: lastSyncAt !== null && Number.isFinite(lastSyncAt) ? lastSyncAt : null,
  });

  // Rebuild the invoice sequence from the invoices already on this device,
  // BEFORE anything can bill.
  //
  // The same recovery ran only at the end of a successful sync. Everything it
  // reads is local, so the network was never the dependency — but a till whose
  // sync was failing never reached it. Meanwhile billing stays available
  // offline by design, so the window between boot and a first good sync is
  // exactly when a counter reset to 0 gets to reissue numbers. One store has
  // five different sales all numbered LTC-00000001 from precisely that window.
  //
  // Best-effort: a failure here must not stop the app from starting. The write
  // path guards itself as well (see useCreateInvoice).
  if (storeId) {
    const slot = Number.isFinite(deviceSlot as number) ? (deviceSlot as number) : null;
    if (slot != null) {
      try {
        const highest = await localHighestSequence(storeId, slot);
        if (highest > 0) await useAppStore.getState().raiseDeviceInvoiceCounter(highest);
      } catch (err) {
        console.warn(
          '[AppStore] Invoice sequence recovery at boot failed:',
          (err as Error).message,
        );
      }
    }
  }
}
